import { Controller } from "@hotwired/stimulus"
import { PillFactory } from '../lib/forms/pill_factory';
import { FieldFactory } from '../lib/forms/field_factory';
import { MembershipTier } from '../lib/forms/membership_tier';
import { MembershipTierFactory } from '../lib/forms/membership_tier_factory';
import { FieldTierVisibilityFactory } from '../lib/forms/field_tier_visibility_factory';
import { Field } from '../lib/forms/field';

export default class extends Controller {
  static targets = [
    'newFieldName',
    'newFieldTypeOption',
    'newFieldCollection',
    'newFieldItemInput',
    'newFieldItemContainer',
    'newFieldRequiredInput',
    'collectionBuilderContainer',
    'customFieldsContainer',
    'membershipTiersContainer',
    'membershipTierVisibility',
    'addMembershipTierButtonContainer',
    'defaultTier',
  ];

  static values = {
    // Template values
    fieldTemplate: String,
    pillTemplate: String,
    membershipTierTemplate: String,
    fieldTierVisibilityTemplate: String,
    // Ordinal state trackers
    currentFieldOrdinal: Number,
    currentMembershipTierOrdinal: Number,
    // Form state trackers
    useMembershipTiers: Boolean
  }

  connect() {
    // Initialize factories
    this.pillFactory = new PillFactory(this.pillTemplateValue);
    this.membershipTierFactory = new MembershipTierFactory(this.membershipTierTemplateValue);
    this.fieldTierVisibilityFactory = new FieldTierVisibilityFactory(this.fieldTierVisibilityTemplateValue);
    this.fieldFactory = new FieldFactory(
      this.fieldTemplateValue,
      this.pillFactory,
      this.fieldTierVisibilityFactory
    );

    // Initialize field instances state
    this.fieldInstances = [];
    this.customFieldsContainerTarget.querySelectorAll('[data-role="customField"]').forEach(
      (fieldElement) => this.fieldInstances.push(new Field(fieldElement, this.pillFactory, this.fieldTierVisibilityFactory))
    );

    // Initialize membership tier instances state
    this.tierInstances = [];
    this.tierInstances.push(new MembershipTier(this.defaultTierTarget));
    this.membershipTiersContainerTarget.querySelectorAll('[data-role="membershipTier"]').forEach(
      (tierElement) => this.tierInstances.push(new MembershipTier(tierElement))
    );
  }

  onKeyEnter(event) {
    if (
      !this.collectionBuilderContainerTarget.classList.contains('d-none')
      && this.newFieldItem
    ) {
      this.addItem(event);
      return;
    }

    this.addField(event);

  }

  addItem(event) {
    if (!this.newFieldItem) {
      this.newFieldItemInputTarget.classList.add('is-invalid');
      return;
    };

    this.newFieldItemInputTarget.classList.remove('is-invalid');

    const pill = this.pillFactory.make(this.newFieldItem, { labelOnly: true });

    this.newFieldItemContainerTarget.appendChild(pill);
    this.newFieldItemInputTarget.value = '';
  }

  addField(event) {
    this.newFieldNameTarget.classList.remove('is-invalid');
    this.newFieldItemInputTarget.classList.remove('is-invalid');

    const fieldName = this.newFieldName;
    const fieldType = this.newFieldType;
    const ordinal = this.nextFieldOrdinal();
    const collection = this.collectItems();
    const required = this.newFieldRequiredInputTarget.checked;

    try {
      const newField = this.fieldFactory.make({
        ordinal,
        collection,
        required,
        name: fieldName,
        type: fieldType,
        tierInstances: this.tierInstances
      });

      if (this.useMembershipTiersValue) {
        newField.showTierVisibility();
      }

      this.customFieldsContainerTarget.appendChild(newField.htmlElement);

      this.newFieldItemContainerTarget.innerHTML = '';
      this.newFieldNameTarget.value = '';

      this.fieldInstances.push(newField);
    } catch (error) {
      console.error(error);

      const reasons = error.reasons || [];

      if (reasons.includes('MISSING_NAME')) {
        this.newFieldNameTarget.classList.add('is-invalid');
      }

      if (reasons.includes('MISSING_COLLECTION')) {
        this.newFieldItemInputTarget.classList.add('is-invalid');
      }
    }
  }

  toggleShowMembershipTiers(event) {
    this.useMembershipTiersValue = event.target.checked;

    if (this.useMembershipTiersValue) {
      this.enableMembershipTiers();
    } else {
      this.disableMembershipTiers();
    }
  }

  enableMembershipTiers() {
    this.addMembershipTierButtonContainerTarget.classList.remove('d-none');
    this.membershipTiersContainerTarget.classList.remove('d-none');
    this.defaultTierTarget.querySelector('[data-role="line"]').classList.remove('d-none');
    this.defaultTierTarget.querySelector('[data-role="name"]').classList.remove('d-none');

    this.fieldInstances.forEach(field => field.showTierVisibility());
  }

  disableMembershipTiers() {
    this.addMembershipTierButtonContainerTarget.classList.add('d-none');
    this.membershipTiersContainerTarget.classList.add('d-none');
    this.defaultTierTarget.querySelector('[data-role="line"]').classList.add('d-none');
    this.defaultTierTarget.querySelector('[data-role="name"]').classList.add('d-none');

    this.fieldInstances.forEach(field => field.hideTierVisibility());
  }

  addMembershipTier(event) {
    const ordinal = this.nextMembershipTierOrdinal();
    const membershipTier = this.membershipTierFactory.make(ordinal);

    // Add the tier to the form
    this.membershipTiersContainerTarget.appendChild(membershipTier.htmlElement);

    this.tierInstances.push(membershipTier);

    this.fieldInstances.forEach(field => {
      field.addTierVisibility(membershipTier);
    });
  }

  collectItems(event) {
    const options = [];

    this.newFieldItemContainerTarget.querySelectorAll('[data-role="item"]').forEach(
      (e) => options.push(e.innerHTML)
    );

    return options;
  }

  removeField(event) {
    const field = event.target.closest('[data-role="customField"]');
    const ordinal = field.dataset.ordinal;

    this.fieldInstances = this.fieldInstances.filter(field => field.ordinal != ordinal);

    field.remove();
  }

  removeTier(event) {
    const tier = event.target.closest('[data-role="membershipTier"]');
    const tierOrdinal = tier.dataset.ordinal;

    this.element.querySelectorAll(`[data-tier-ordinal="${tierOrdinal}"]`).forEach(pill => {
      pill.remove();
    });

    tier.remove();
  }

  removePill(event) {
    event.target.closest('div[data-role="pill"]').remove();
  }

  showCollectionBuilder(event) {
    this.collectionBuilderContainerTarget.classList.remove('d-none');
  }

  hideCollectionBuilder(event) {
    if (!event.target.checked) return;

    this.collectionBuilderContainerTarget.classList.add('d-none');
  }

  nextFieldOrdinal() {
    return ++this.currentFieldOrdinalValue;
  }

  nextMembershipTierOrdinal() {
    return ++this.currentMembershipTierOrdinalValue;
  }

  get newFieldType() {
    return this.newFieldTypeOptionTargets.find(f => f.checked).value
  }

  get newFieldName() {
    return this.newFieldNameTarget.value
  }

  get newFieldItem() {
    return this.newFieldItemInputTarget.value
  }

  createTierCheckbox(name, fieldIndex, tierId) {
    const div = document.createElement('div');
    div.className = 'tier-pill inactive';
    div.dataset.tierPillTarget = 'pill';
    div.dataset.action = 'click->tier-pill#toggle';
    div.dataset.tierName = name;
    div.dataset.fieldIndex = fieldIndex;
    div.textContent = name;

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.name = `membership[fields_attributes][${fieldIndex}][membership_tier_names][]`;
    input.value = name;
    input.className = 'd-none';
    input.checked = false;

    div.appendChild(input);
    return div;
  }
}